'use client';

import React, { useEffect, useState, useCallback, useRef } from 'react';
import Link from 'next/link';
import {
  CheckCircle,
  Shield,
  Zap,
  ArrowRight,
  Wallet,
  Clock,
  BarChart3,
  Send,
  ChevronDown,
  Ticket,
  Calendar,
  UserCheck,
  MapPin,
  Heart,
  TrendingUp,
  Users,
  Globe,
} from 'lucide-react';

function BatchPaymentMockup() {
  return (
    <div className="relative w-full max-w-lg mx-auto">
      <div className="absolute -inset-4 bg-gradient-to-br from-white/10 to-white/5 rounded-3xl blur-xl" />

      <div className="relative bg-white/[0.08] backdrop-blur-sm border border-white/20 rounded-2xl p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-400/80" />
            <div className="w-3 h-3 rounded-full bg-yellow-400/80" />
            <div className="w-3 h-3 rounded-full bg-green-400/80" />
          </div>
          <div className="text-xs text-white/50 font-medium">Batch Payment System</div>
          <div className="w-16" />
        </div>

        <div className="grid grid-cols-3 gap-3 mb-4">
          {[
            { label: 'Total Disbursed', value: 'TZS 6.2B', change: '+12.5%', positive: true },
            { label: 'Active Batches', value: '148', change: '+8.2%', positive: true },
            { label: 'Pending Approvals', value: '23', change: '-3.1%', positive: false },
          ].map((stat) => (
            <div key={stat.label} className="bg-white/[0.07] border border-white/10 rounded-xl p-3">
              <p className="text-[10px] text-white/50 uppercase tracking-wider mb-1">{stat.label}</p>
              <p className="text-lg font-bold text-white font-tabular">{stat.value}</p>
              <p className={`text-[10px] font-semibold mt-1 ${stat.positive ? 'text-emerald-300' : 'text-amber-300'}`}>
                {stat.change}
              </p>
            </div>
          ))}
        </div>

        <div className="bg-white/[0.05] border border-white/10 rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-white/70">Payment Flow</span>
            <span className="text-[10px] text-emerald-300 bg-emerald-400/10 px-2 py-0.5 rounded-full">Live</span>
          </div>
          <div className="flex items-center justify-between">
            {['Request', 'Approve', 'Fund', 'Payout'].map((step, idx) => (
              <React.Fragment key={step}>
                <div className="flex flex-col items-center gap-1.5">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                      idx < 2
                        ? 'bg-emerald-400 text-emerald-950'
                        : idx === 2
                        ? 'bg-amber-400 text-amber-950 animate-pulse'
                        : 'bg-white/10 text-white/40'
                    }`}
                  >
                    {idx < 2 ? <CheckCircle size={14} /> : idx === 2 ? <Clock size={14} /> : idx + 1}
                  </div>
                  <span className={`text-[10px] font-medium ${idx === 3 ? 'text-white/30' : 'text-white/70'}`}>
                    {step}
                  </span>
                </div>
                {idx < 3 && (
                  <div className={`flex-1 h-0.5 mx-1 rounded ${idx < 2 ? 'bg-emerald-400/60' : 'bg-white/10'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="bg-white/[0.05] border border-white/10 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-white/70">Recent Payouts</span>
            <BarChart3 size={14} className="text-white/40" />
          </div>
          <div className="space-y-2">
            {[
              { name: 'Mission Alpha', amount: 'TZS 32,000,000', status: 'Completed' },
              { name: 'Field Ops - Zone 3', amount: 'TZS 22,750,000', status: 'Processing' },
              { name: 'Team Bravo Disb.', amount: 'TZS 13,520,000', status: 'Completed' },
            ].map((payout) => (
              <div key={payout.name} className="flex items-center justify-between py-1.5">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-md bg-white/10 flex items-center justify-center">
                    <Send size={10} className="text-emerald-300" />
                  </div>
                  <span className="text-xs text-white/70">{payout.name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-semibold text-white font-tabular">{payout.amount}</span>
                  <span
                    className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
                      payout.status === 'Completed' ? 'bg-emerald-400/15 text-emerald-300' : 'bg-amber-400/15 text-amber-300'
                    }`}
                  >
                    {payout.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="absolute -top-3 -right-3 bg-emerald-400 text-emerald-950 rounded-xl px-3 py-2 shadow-lg animate-bounce">
        <div className="flex items-center gap-1.5">
          <Wallet size={14} />
          <span className="text-xs font-bold">+ TZS 63.7M</span>
        </div>
      </div>
    </div>
  );
}

function EventPassMockup() {
  return (
    <div className="relative w-full max-w-lg mx-auto">
      <div className="absolute -inset-4 bg-gradient-to-br from-white/10 to-white/5 rounded-3xl blur-xl" />

      <div className="relative bg-white/[0.08] backdrop-blur-sm border border-white/20 rounded-2xl p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-400/80" />
            <div className="w-3 h-3 rounded-full bg-yellow-400/80" />
            <div className="w-3 h-3 rounded-full bg-green-400/80" />
          </div>
          <div className="text-xs text-white/50 font-medium">Ceremony Dashboard</div>
          <div className="w-16" />
        </div>

        <div className="bg-white/[0.06] border border-white/10 rounded-xl p-3 mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Heart size={14} className="text-emerald-300" />
              <span className="text-sm font-semibold text-white">Wedding Ceremony</span>
            </div>
            <span className="text-[10px] text-emerald-300 bg-emerald-400/10 px-2 py-0.5 rounded-full">Active</span>
          </div>
          <div className="flex items-center gap-3 text-[10px] text-white/50">
            <span className="flex items-center gap-1"><Calendar size={9} /> Aug 15, 2026</span>
            <span className="flex items-center gap-1"><Users size={9} /> 250 guests</span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-4">
          {[
            { label: 'Invitations Sent', value: '218', sub: '32 pending' },
            { label: 'Contributions', value: 'TZS 1.2B', sub: 'of 2M target' },
            { label: 'Contributors', value: '147', sub: 'of 218 invited' },
          ].map((stat) => (
            <div key={stat.label} className="bg-white/[0.07] border border-white/10 rounded-xl p-3">
              <p className="text-[10px] text-white/50 uppercase tracking-wider mb-1">{stat.label}</p>
              <p className="text-lg font-bold text-white">{stat.value}</p>
              <p className="text-[10px] font-medium mt-1 text-emerald-300/80">{stat.sub}</p>
            </div>
          ))}
        </div>

        <div className="bg-white/[0.05] border border-white/10 rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-white/70">Invitation Status</span>
            <Send size={14} className="text-white/40" />
          </div>
          <div className="space-y-2">
            {[
              { label: 'Sent & Delivered', count: 186, color: 'bg-emerald-400', width: 'w-[85%]' },
              { label: 'Pending Delivery', count: 32, color: 'bg-amber-400', width: 'w-[15%]' },
              { label: 'RSVP Confirmed', count: 142, color: 'bg-blue-400', width: 'w-[65%]' },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-3">
                <span className="text-[10px] text-white/60 w-28 flex-shrink-0">{item.label}</span>
                <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                  <div className={`h-full ${item.color} rounded-full ${item.width}`} />
                </div>
                <span className="text-[10px] text-white/50 w-8 text-right">{item.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white/[0.05] border border-white/10 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-white/70">Recent Contributions</span>
            <TrendingUp size={14} className="text-white/40" />
          </div>
          <div className="space-y-2">
            {[
              { name: 'James Mwangi', amount: 'TZS 10,000', time: '2 min ago' },
              { name: 'Grace Wanjiku', amount: 'TZS 5,000', time: '8 min ago' },
              { name: 'Peter Ochieng', amount: 'TZS 15,000', time: '15 min ago' },
            ].map((contrib) => (
              <div key={contrib.name} className="flex items-center justify-between py-1">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-emerald-400/15 flex items-center justify-center">
                    <span className="text-[9px] font-bold text-emerald-300">{contrib.name.charAt(0)}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs text-white/70">{contrib.name}</span>
                    <span className="text-[9px] text-white/40">{contrib.time}</span>
                  </div>
                </div>
                <span className="text-xs font-semibold text-emerald-300">{contrib.amount}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="absolute -top-3 -right-3 bg-emerald-400 text-emerald-950 rounded-xl px-3 py-2 shadow-lg animate-bounce">
        <div className="flex items-center gap-1.5">
          <Heart size={14} />
          <span className="text-xs font-bold">+ TZS 10K</span>
        </div>
      </div>
    </div>
  );
}

export default function HeroSlideshow() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const totalSlides = 3;

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, 6000);
  }, []);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    startTimer();
  }, [startTimer]);

  useEffect(() => {
    startTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [startTimer]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    resetTimer();
  };

  const scrollToFeatures = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const el = document.getElementById('features');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const slideLabels = ['Digital Solutions', 'Batch Payments', 'Event Passes'];

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[var(--primary)] via-[var(--primary-light)] to-[#388E3C] min-h-screen flex items-center" style={{width: '100vw', marginLeft: 'calc(-50vw + 50%)', marginRight: 'calc(-50vw + 50%)'}}>
      <div className="absolute inset-0 opacity-10">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="hero-grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#hero-grid)" />
        </svg>
      </div>

      <div className="absolute top-20 left-10 w-72 h-72 bg-white/5 rounded-full blur-3xl" />
      <div className="absolute bottom-10 right-20 w-96 h-96 bg-emerald-300/10 rounded-full blur-3xl" />

      <div className="relative z-10 w-full h-full px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="relative w-full h-full">
          <div
            className={`absolute inset-0 transition-opacity duration-700 ease-in-out flex items-center justify-center ${
              currentSlide === 0 ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
            }`}
          >
            <div className="max-w-4xl mx-auto text-center">
              <div
                className={`space-y-8 transition-all duration-1000 ease-out ${
                  visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
              >
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5">
                  <Zap size={14} className="text-emerald-300" />
                  <span className="text-xs font-semibold text-white/90 tracking-wide">
                    Enterprise-Grade Digital Solutions
                  </span>
                </div>

                <h1 className="text-hero-xl font-extrabold text-white leading-tight tracking-tight">
                  Transform Your Business{' '}
                  <span className="relative inline-block">
                    <span className="relative z-10 text-emerald-300">Digital</span>
                    <span className="absolute bottom-1 left-0 right-0 h-2 bg-emerald-400/20 rounded" />
                  </span>{' '}
                  Solutions
                </h1>

                <p className="text-lg text-white/80 max-w-2xl mx-auto leading-relaxed">
                  From batch payments to event management, we provide comprehensive digital solutions that empower organizations across Africa.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    href="/login-screen"
                    className="inline-flex items-center justify-center gap-2 bg-white text-[var(--primary)] font-bold px-8 py-3.5 rounded-xl hover:bg-emerald-50 transition-all duration-200 shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/25 hover:-translate-y-0.5 group"
                  >
                    Get Started
                    <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                  </Link>
                  <a
                    href="#features"
                    onClick={scrollToFeatures}
                    className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm border border-white/25 text-white font-bold px-8 py-3.5 rounded-xl hover:bg-white/20 transition-all duration-200 hover:-translate-y-0.5"
                  >
                    Learn More
                    <ChevronDown size={18} />
                  </a>
                </div>

                <div className="flex flex-wrap gap-x-8 gap-y-3 pt-4 justify-center">
                  <div className="flex items-center gap-2 text-white/90 text-sm">
                    <Globe size={16} className="text-emerald-300" />
                    <span>Built for African businesses</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/90 text-sm">
                    <Shield size={16} className="text-emerald-300" />
                    <span>99.9% uptime</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/90 text-sm">
                    <CheckCircle size={16} className="text-emerald-300" />
                    <span>Bank-level security</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            className={`absolute inset-0 transition-opacity duration-700 ease-in-out flex items-center ${
              currentSlide === 1 ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
            }`}
          >
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5">
                  <Wallet size={14} className="text-emerald-300" />
                  <span className="text-xs font-semibold text-white/90 tracking-wide">
                    Batch Payment System
                  </span>
                </div>
                <h2 className="text-4xl lg:text-5xl font-extrabold text-white leading-tight tracking-tight">
                  Streamline Your{' '}
                  <span className="text-emerald-300">Payment</span>{' '}
                  Operations
                </h2>
                <p className="text-lg text-white/80 leading-relaxed">
                  Process thousands of payments in batches with automated workflows, real-time tracking, and comprehensive audit trails.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-emerald-300 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-white">Multi-Step Approval Workflow</p>
                      <p className="text-sm text-white/70">Request, approve, fund, and payout with full control</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-emerald-300 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-white">Real-Time Analytics</p>
                      <p className="text-sm text-white/70">Track disbursements, batches, and pending approvals</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-emerald-300 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-white">Bank-Grade Security</p>
                      <p className="text-sm text-white/70">Enterprise-level encryption and compliance</p>
                    </div>
                  </div>
                </div>
                <Link
                  href="/login-screen"
                  className="inline-flex items-center gap-2 bg-white text-[var(--primary)] font-bold px-6 py-3 rounded-xl hover:bg-emerald-50 transition-all duration-200 shadow-lg hover:-translate-y-0.5 group"
                >
                  Try Batch Payments
                  <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
              <div className="w-full">
                <BatchPaymentMockup />
              </div>
            </div>
          </div>

          <div
            className={`absolute inset-0 transition-opacity duration-700 ease-in-out flex items-center ${
              currentSlide === 2 ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
            }`}
          >
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5">
                  <Ticket size={14} className="text-emerald-300" />
                  <span className="text-xs font-semibold text-white/90 tracking-wide">
                    Event Pass System
                  </span>
                </div>
                <h2 className="text-4xl lg:text-5xl font-extrabold text-white leading-tight tracking-tight">
                  Manage Events &{' '}
                  <span className="text-emerald-300">Contributions</span>{' '}
                  Effortlessly
                </h2>
                <p className="text-lg text-white/80 leading-relaxed">
                  From weddings to community gatherings, manage invitations, track RSVPs, and collect contributions seamlessly.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-emerald-300 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-white">Digital Invitations</p>
                      <p className="text-sm text-white/70">Send beautiful cards via SMS, email, or WhatsApp</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-emerald-300 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-white">Contribution Tracking</p>
                      <p className="text-sm text-white/70">Monitor donations and contributor progress in real-time</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-emerald-300 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-white">RSVP Management</p>
                      <p className="text-sm text-white/70">Track attendance and manage guest lists efficiently</p>
                    </div>
                  </div>
                </div>
                <Link
                  href="/login-screen"
                  className="inline-flex items-center gap-2 bg-white text-[var(--primary)] font-bold px-6 py-3 rounded-xl hover:bg-emerald-50 transition-all duration-200 shadow-lg hover:-translate-y-0.5 group"
                >
                  Try Event Passes
                  <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
              <div className="w-full">
                <EventPassMockup />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 z-20">
        <div className="flex items-center justify-center gap-2 pb-6">
          {Array.from({ length: totalSlides }).map((_, idx) => (
            <button
              key={idx}
              onClick={() => goToSlide(idx)}
              className={`transition-all duration-300 rounded-full ${
                currentSlide === idx
                  ? 'w-8 h-2.5 bg-white'
                  : 'w-2.5 h-2.5 bg-white/30 hover:bg-white/50'
              }`}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>

        <div className="flex items-center justify-center gap-4 pb-8">
          {slideLabels.map((label, idx) => (
            <React.Fragment key={label}>
              {idx > 0 && <span className="text-white/20">|</span>}
              <span
                className={`text-xs font-medium transition-colors duration-300 ${
                  currentSlide === idx ? 'text-white' : 'text-white/40'
                }`}
              >
                {label}
              </span>
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[var(--background)] to-transparent z-20" />
    </section>
  );
}
